// Carries a well list input from the filter thunk to the map. Only the input
// arms it, so a lasso, an XDA filter-to-well or a restore that happens to
// match one well never opens a card.
import { create } from "zustand";

import { MapExtent } from "api/useTownshipRangeGrid";

export interface WellListOpen {
  readonly uwi: string;
  readonly extent: MapExtent;
  readonly at: number;
  /** Taken at the user's apply, so a later jump wins over a late result. */
  readonly token: number;
}

interface WellListArm {
  readonly listId: string;
  readonly token: number;
}

export interface WellListOpenStore {
  arm: Nullable<WellListArm>;
  /** Kept after it is claimed: the auto-zoom reads it to skip this extent. */
  pending: Maybe<WellListOpen>;
  claimed: Maybe<WellListOpen>;
  /** The open the claiming map flies to. Only then does the auto-zoom skip it. */
  flying: Maybe<WellListOpen>;
  setArm: (listId: Maybe<string>, token: number) => void;
  /** Spent by the first run that carries the armed list, whatever its outcome. */
  take: (listId: Maybe<string>) => Nullable<number>;
  setPending: (pending: Maybe<WellListOpen>) => void;
  /** True once per open, so two maps open one card. */
  claim: (open: WellListOpen) => boolean;
  setFlying: (open: WellListOpen) => void;
  reset: () => void;
}

export const useWellListOpenStore = create<WellListOpenStore>()((set, get) => ({
  arm: null,
  pending: undefined,
  claimed: undefined,
  flying: undefined,
  setArm: (listId, token) => set({ arm: listId ? { listId, token } : null }),
  take: (listId) => {
    const { arm } = get();
    if (!listId || arm?.listId !== listId) return null;
    set({ arm: null });
    return arm.token;
  },
  setPending: (pending) => {
    if (get().pending !== pending) set({ pending });
  },
  claim: (open) => {
    if (get().claimed === open) return false;
    set({ claimed: open });
    return true;
  },
  setFlying: (open) => set({ flying: open }),
  reset: () =>
    set({ arm: null, pending: undefined, claimed: undefined, flying: undefined })
}));

export const selectWellListOpen = (s: WellListOpenStore): Maybe<WellListOpen> =>
  s.pending;
